import { useMemo, useState } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
} from "recharts";
import {
    CalendarRange,
    CalendarCheck,
    CheckCircle2,
    XCircle,
    IndianRupee,
    Download,
    TrendingUp,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Sample data generator — replace with your real bookings feed.
// Produces one record per day for the last N days: how many bookings were
// active/upcoming, closed/completed, or cancelled that day, plus revenue.
// ---------------------------------------------------------------------------
function seededValue(seed, min, max) {
    const x = Math.sin(seed * 999) * 10000;
    const frac = x - Math.floor(x);
    return Math.floor(min + frac * (max - min));
}

function generateDailyData(days = 60) {
    const rows = [];
    const today = new Date("2026-07-21T00:00:00");
    for (let i = days - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const seed = days - i;
        const closed = seededValue(seed, 2, 12);
        const active = seededValue(seed + 50, 1, 8);
        const cancelled = seededValue(seed + 100, 0, 3);
        const avgPrice = seededValue(seed + 150, 1200, 2200);
        rows.push({
            date: d.toISOString().slice(0, 10),
            label: d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
            active,
            closed,
            cancelled,
            revenue: closed * avgPrice,
        });
    }
    return rows;
}

const DAILY_DATA = generateDailyData(60);

const STATUS_COLORS = {
    Active: "#4f46e5",
    Closed: "#10b981",
    Cancelled: "#f43f5e",
};

const QUICK_RANGES = [
    { label: "7D", days: 7 },
    { label: "30D", days: 30 },
    { label: "60D", days: 60 },
];

function formatINR(n) {
    return `₹${n.toLocaleString("en-IN")}`;
}

// ---------------------------------------------------------------------------
// Summary card
// ---------------------------------------------------------------------------
function StatCard({ icon: Icon, label, value, accent, sub }) {
    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
                <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${accent.bg}`}>
                    <Icon className={`h-4 w-4 ${accent.text}`} />
                </div>
            </div>
            <p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
            {sub && <p className="mt-0.5 text-xs text-slate-400">{sub}</p>}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function ReportsAnalytics() {
    const minDate = DAILY_DATA[0].date;
    const maxDate = DAILY_DATA[DAILY_DATA.length - 1].date;

    const [startDate, setStartDate] = useState(DAILY_DATA[DAILY_DATA.length - 30].date);
    const [endDate, setEndDate] = useState(maxDate);

    const applyQuickRange = (days) => {
        setStartDate(DAILY_DATA[Math.max(0, DAILY_DATA.length - days)].date);
        setEndDate(maxDate);
    };

    const filtered = useMemo(
        () => DAILY_DATA.filter((r) => r.date >= startDate && r.date <= endDate),
        [startDate, endDate]
    );

    const totals = useMemo(() => {
        const active = filtered.reduce((s, r) => s + r.active, 0);
        const closed = filtered.reduce((s, r) => s + r.closed, 0);
        const cancelled = filtered.reduce((s, r) => s + r.cancelled, 0);
        const revenue = filtered.reduce((s, r) => s + r.revenue, 0);
        return { active, closed, cancelled, revenue, total: active + closed + cancelled };
    }, [filtered]);

    const pieData = [
        { name: "Active", value: totals.active },
        { name: "Closed", value: totals.closed },
        { name: "Cancelled", value: totals.cancelled },
    ];

    const exportCSV = () => {
        const headers = ["Date", "Active", "Closed", "Cancelled", "Revenue (INR)"];
        const lines = filtered.map((r) => [r.date, r.active, r.closed, r.cancelled, r.revenue].join(","));
        const csv = [headers.join(","), ...lines].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `bookings-report-${startDate}_to_${endDate}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="h-screen w-full overflow-y-auto bg-slate-50 p-3 sm:p-5">
            <div className="mx-auto w-full max-w-[1600px]">
                {/* Top bar: date range + quick ranges + export */}
                <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                        <CalendarRange className="h-4 w-4 text-slate-400" />
                        <input
                            type="date"
                            value={startDate}
                            min={minDate}
                            max={endDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                        <span className="text-sm text-slate-400">to</span>
                        <input
                            type="date"
                            value={endDate}
                            min={startDate}
                            max={maxDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                        <div className="ml-1 flex gap-1">
                            {QUICK_RANGES.map((q) => (
                                <button
                                    key={q.label}
                                    onClick={() => applyQuickRange(q.days)}
                                    className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:border-indigo-300 hover:text-indigo-600 transition-colors"
                                >
                                    {q.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <button
                        onClick={exportCSV}
                        className="inline-flex items-center gap-2 self-start rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors sm:self-auto"
                    >
                        <Download className="h-4 w-4" />
                        Export
                    </button>
                </div>

                {/* Summary cards */}
                <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <StatCard
                        icon={CalendarCheck}
                        label="Active bookings"
                        value={totals.active}
                        accent={{ bg: "bg-indigo-50", text: "text-indigo-600" }}
                        sub={`${startDate} to ${endDate}`}
                    />
                    <StatCard
                        icon={CheckCircle2}
                        label="Closed bookings"
                        value={totals.closed}
                        accent={{ bg: "bg-emerald-50", text: "text-emerald-600" }}
                        sub="Completed sessions"
                    />
                    <StatCard
                        icon={XCircle}
                        label="Cancelled"
                        value={totals.cancelled}
                        accent={{ bg: "bg-rose-50", text: "text-rose-600" }}
                        sub="Dropped bookings"
                    />
                    <StatCard
                        icon={IndianRupee}
                        label="Revenue"
                        value={formatINR(totals.revenue)}
                        accent={{ bg: "bg-violet-50", text: "text-violet-600" }}
                        sub={`From ${totals.closed} closed sessions`}
                    />
                </div>

                {/* Charts row */}
                <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
                    {/* Daily bookings bar chart */}
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 lg:col-span-2">
                        <div className="mb-3 flex items-center justify-between">
                            <p className="text-sm font-semibold text-slate-900">Daily bookings</p>
                            <p className="text-xs text-slate-400">Active vs Closed vs Cancelled</p>
                        </div>
                        <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={filtered} barGap={2}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis
                                    dataKey="label"
                                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                                    interval={Math.max(0, Math.floor(filtered.length / 10))}
                                    axisLine={{ stroke: "#e2e8f0" }}
                                    tickLine={false}
                                />
                                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                                <Tooltip
                                    contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                                />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="active" name="Active" fill={STATUS_COLORS.Active} radius={[4, 4, 0, 0]} />
                                <Bar dataKey="closed" name="Closed" fill={STATUS_COLORS.Closed} radius={[4, 4, 0, 0]} />
                                <Bar dataKey="cancelled" name="Cancelled" fill={STATUS_COLORS.Cancelled} radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Status distribution donut */}
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                        <p className="mb-3 text-sm font-semibold text-slate-900">Status distribution</p>
                        <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius={55}
                                    outerRadius={85}
                                    paddingAngle={3}
                                >
                                    {pieData.map((entry) => (
                                        <Cell key={entry.name} fill={STATUS_COLORS[entry.name]} />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="mt-2 space-y-2">
                            {pieData.map((entry) => (
                                <div key={entry.name} className="flex items-center justify-between text-sm">
                                    <span className="flex items-center gap-2 text-slate-600">
                                        <span
                                            className="h-2.5 w-2.5 rounded-full"
                                            style={{ backgroundColor: STATUS_COLORS[entry.name] }}
                                        />
                                        {entry.name}
                                    </span>
                                    <span className="font-medium text-slate-900">
                                        {entry.value}
                                        <span className="ml-1 text-xs text-slate-400">
                                            ({totals.total ? Math.round((entry.value / totals.total) * 100) : 0}%)
                                        </span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Revenue trend */}
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="mb-3 flex items-center justify-between">
                        <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                            <TrendingUp className="h-4 w-4 text-indigo-600" />
                            Revenue trend
                        </p>
                        <p className="text-xs text-slate-400">Daily revenue from closed sessions</p>
                    </div>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={filtered}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis
                                dataKey="label"
                                tick={{ fontSize: 11, fill: "#94a3b8" }}
                                interval={Math.max(0, Math.floor(filtered.length / 10))}
                                axisLine={{ stroke: "#e2e8f0" }}
                                tickLine={false}
                            />
                            <YAxis
                                tick={{ fontSize: 11, fill: "#94a3b8" }}
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={(v) => `₹${v / 1000}k`}
                            />
                            <Tooltip
                                formatter={(v) => formatINR(v)}
                                contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="revenue"
                                name="Revenue"
                                stroke="#7c3aed"
                                strokeWidth={2.5}
                                dot={false}
                                activeDot={{ r: 5 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}